import { useEffect, useRef, useState } from 'react'
import { io } from 'socket.io-client'

// Una sola conexión socket.io para toda la app (singleton). Path /api/socket.io para
// pasar por el mismo proxy que la API REST. Auth con el JWT guardado en localStorage.
let _socket = null
export function getSocket() {
  if (!_socket) {
    _socket = io({
      path: '/api/socket.io',
      auth: { token: localStorage.getItem('token') },
      // polling PRIMERO (funciona por el proxy de /api sin necesidad de cabeceras WS en
      // nginx); luego, si puede, sube a websocket. Más compatible que forzar websocket.
      transports: ['polling', 'websocket'],
    })
    // Si el token cambió (re-login), refrescarlo antes de reconectar.
    _socket.on('reconnect_attempt', () => { _socket.auth = { token: localStorage.getItem('token') } })
    // Diagnóstico: si la conexión falla (auth, ruta, nginx…), queda en consola.
    // Si la rechaza el SERVIDOR (no la red), socket.io no lo vuelve a intentar solo (A2: con la base sin contestar, el
    // servidor no abre): se reintenta en 15 s mientras haya sesión, como el de las llamadas.
    _socket.on('connect_error', (e) => {
      console.warn('[presencia] socket connect_error:', e?.message || e)
      if (!_socket.active) setTimeout(() => {
        if (_socket && !_socket.connected && localStorage.getItem('token')) { _socket.auth = { token: localStorage.getItem('token') }; _socket.connect() }
      }, 15000)
    })
  }
  return _socket
}

// Presencia en tiempo real de la conversación `convId`: quién más la está viendo y quién
// está escribiendo. `miId` = id del agente actual (para excluirse a sí mismo).
// Devuelve { presentes, escribiendo, notifyTyping }.
export function useConvPresencia(convId, miId) {
  const [presentes, setPresentes] = useState([])     // [{id, nombre}] otros que la ven
  const [escribiendo, setEscribiendo] = useState([]) // [{id, nombre}] otros escribiendo
  const typingTimers = useRef({})

  useEffect(() => {
    if (!convId) { setPresentes([]); setEscribiendo([]); return }
    const socket = getSocket()
    const join = () => socket.emit('conv:join', convId)
    join()
    socket.on('connect', join) // re-unirse tras una reconexión

    const onPresencia = ({ convId: cid, agentes }) => {
      if (String(cid) !== String(convId)) return
      // Excluir MI propia sesión por socketId (no por cuenta: dos personas pueden compartir
      // la misma cuenta en equipos distintos y aun así hay que avisarse).
      setPresentes((agentes || []).filter(a => a.socketId !== socket.id))
    }
    const onTyping = ({ convId: cid, agente, typing, socketId }) => {
      if (String(cid) !== String(convId) || !agente) return
      // El backend ya excluye al emisor (socket.to), así que NO filtramos por cuenta propia.
      // Cada sesión se distingue por socketId (soporta cuenta compartida en 2 equipos).
      const key = socketId || agente.id
      setEscribiendo(prev => {
        const otros = prev.filter(a => a._key !== key)
        return typing ? [...otros, { ...agente, _key: key }] : otros
      })
      clearTimeout(typingTimers.current[key])
      if (typing) {
        typingTimers.current[key] = setTimeout(() => {
          setEscribiendo(prev => prev.filter(a => a._key !== key))
        }, 5000) // se apaga solo si dejan de llegar pulsaciones
      }
    }
    socket.on('conv:presencia', onPresencia)
    socket.on('conv:typing', onTyping)

    return () => {
      socket.emit('conv:leave', convId)
      socket.off('connect', join)
      socket.off('conv:presencia', onPresencia)
      socket.off('conv:typing', onTyping)
      Object.values(typingTimers.current).forEach(clearTimeout)
      typingTimers.current = {}
      setPresentes([]); setEscribiendo([])
    }
  }, [convId, miId])

  // Avisar de "escribiendo" con throttle simple + auto-apagado a los 2.5s sin teclear.
  const lastTyping = useRef(0)
  const stopTimer = useRef(null)
  const notifyTyping = () => {
    if (!convId) return
    const socket = getSocket()
    const now = Date.now()
    if (now - lastTyping.current > 1500) {
      lastTyping.current = now
      socket.emit('conv:typing', { convId, typing: true })
    }
    clearTimeout(stopTimer.current)
    stopTimer.current = setTimeout(() => {
      socket.emit('conv:typing', { convId, typing: false })
      lastTyping.current = 0
    }, 2500)
  }

  return { presentes, escribiendo, notifyTyping }
}
